import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Key, Copy, Check, Eye, EyeOff, Pencil, Trash2, Plus, X, ArrowLeft, Lock } from 'lucide-react';
import type {
  CustomPassword,
  CustomPasswordInput,
  FileCategory,
  PasswordsOverview,
} from '../hooks/useSupabaseFiles';

export interface PasswordApi {
  listPasswords: () => Promise<PasswordsOverview>;
  saveCustomPassword: (id: string | null, input: CustomPasswordInput) => Promise<CustomPassword>;
  deleteCustomPassword: (id: string) => Promise<void>;
}

type View = { kind: 'list' } | { kind: 'form'; editing: CustomPassword | null };

function useCopyFeedback() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copy = useCallback((key: string, value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
      })
      .catch(() => setCopiedKey(null));
  }, []);
  return { copiedKey, copy };
}

function SecretValue({
  value,
  revealed,
  onToggleReveal,
  copied,
  onCopy,
}: {
  value: string;
  revealed: boolean;
  onToggleReveal: () => void;
  copied: boolean;
  onCopy: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 min-w-0 truncate px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm font-mono">
        {revealed ? value : '••••••••'}
      </code>
      <button
        onClick={onToggleReveal}
        title={revealed ? t('files.passwords.hide') : t('files.passwords.show')}
        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all"
      >
        {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
      <button
        onClick={onCopy}
        title={t('files.passwords.copy')}
        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all"
      >
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function CustomPasswordForm({
  editing,
  onCancel,
  onSaved,
  api,
}: {
  editing: CustomPassword | null;
  onCancel: () => void;
  onSaved: () => void;
  api: PasswordApi;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(editing?.label ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [password, setPassword] = useState(editing?.password_plain ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = label.trim().length > 0 && password.length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await api.saveCustomPassword(editing?.id ?? null, {
        label: label.trim(),
        description: description.trim(),
        password,
      });
      onSaved();
    } catch {
      setError(t('files.passwords.saveFailed'));
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#EE1C23]/50 focus:outline-none';

  return (
    <div className="space-y-4">
      <div>
        <label className="text-gray-400 text-sm mb-1 block">{t('files.passwords.label')}</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('files.passwords.labelPlaceholder')} className={inputClass} />
      </div>
      <div>
        <label className="text-gray-400 text-sm mb-1 block">{t('files.passwords.description')}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('files.passwords.descriptionPlaceholder')}
          rows={3}
          className={`${inputClass} resize-none`}
        />
      </div>
      <div>
        <label className="text-gray-400 text-sm mb-1 block">{t('files.passwords.value')}</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`${inputClass} pr-10 font-mono`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <div className="flex gap-3 pt-2">
        <button onClick={onCancel} className="flex-1 px-4 py-2 rounded-lg bg-white/10 text-gray-300 text-sm font-medium hover:bg-white/20 transition-all">
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-40"
        >
          {saving ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </div>
  );
}

export default function PasswordManagerModal({
  api,
  categories,
  onClose,
}: {
  api: PasswordApi;
  categories: FileCategory[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { listPasswords, deleteCustomPassword } = api;
  const { copiedKey, copy } = useCopyFeedback();
  const [overview, setOverview] = useState<PasswordsOverview | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      setOverview(await listPasswords());
    } catch {
      setLoadFailed(true);
    }
  }, [listPasswords]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleReveal = (key: string) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDelete = async (entry: CustomPassword) => {
    if (!window.confirm(t('files.passwords.confirmDelete', { label: entry.label }))) return;
    try {
      await deleteCustomPassword(entry.id);
      await load();
    } catch {
      setLoadFailed(true);
    }
  };

  const categoryName = (categoryId: string) => categories.find((c) => c.id === categoryId)?.name ?? '';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-gray-900 border border-[#EE1C23]/30 rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {view.kind === 'form' && (
              <button onClick={() => setView({ kind: 'list' })} className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Key className="w-5 h-5 text-[#EE1C23]" />
            <h2 className="text-xl font-bold text-white">
              {view.kind === 'form'
                ? view.editing
                  ? t('files.passwords.editTitle')
                  : t('files.passwords.addTitle')
                : t('files.passwords.title')}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {view.kind === 'form' ? (
          <CustomPasswordForm
            editing={view.editing}
            api={api}
            onCancel={() => setView({ kind: 'list' })}
            onSaved={async () => {
              setView({ kind: 'list' });
              await load();
            }}
          />
        ) : loadFailed && !overview ? (
          <p className="text-red-400 text-sm">{t('files.passwords.loadFailed')}</p>
        ) : !overview ? (
          <p className="text-gray-400 text-sm">{t('common.loading')}</p>
        ) : (
          <div className="space-y-8">
            <section>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide mb-3">{t('files.passwords.filesSection')}</h3>
              {overview.files.length === 0 ? (
                <p className="text-gray-500 text-sm">{t('files.passwords.noFiles')}</p>
              ) : (
                <ul className="space-y-3">
                  {overview.files.map((file) => {
                    const key = `file-${file.id}`;
                    return (
                      <li key={file.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="mb-2">
                          <p className="text-white text-sm font-medium">{file.name}</p>
                          {categoryName(file.category_id) && (
                            <p className="text-gray-500 text-xs">{categoryName(file.category_id)}</p>
                          )}
                        </div>
                        {file.password_plain ? (
                          <SecretValue
                            value={file.password_plain}
                            revealed={revealed.has(key)}
                            onToggleReveal={() => toggleReveal(key)}
                            copied={copiedKey === key}
                            onCopy={() => copy(key, file.password_plain ?? '')}
                          />
                        ) : (
                          <p className="text-gray-500 text-xs italic">{t('files.passwords.notStored')}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">{t('files.passwords.customSection')}</h3>
                <button
                  onClick={() => setView({ kind: 'form', editing: null })}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-xs font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('files.passwords.add')}
                </button>
              </div>
              {overview.custom.length === 0 ? (
                <p className="text-gray-500 text-sm">{t('files.passwords.noCustom')}</p>
              ) : (
                <ul className="space-y-3">
                  {overview.custom.map((entry) => {
                    const key = `custom-${entry.id}`;
                    return (
                      <li key={entry.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="min-w-0">
                            <p className="text-white text-sm font-medium truncate">{entry.label}</p>
                            {entry.description && <p className="text-gray-400 text-xs mt-1 whitespace-pre-line">{entry.description}</p>}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => setView({ kind: 'form', editing: entry })}
                              title={t('common.edit')}
                              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(entry)}
                              title={t('common.delete')}
                              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <SecretValue
                          value={entry.password_plain}
                          revealed={revealed.has(key)}
                          onToggleReveal={() => toggleReveal(key)}
                          copied={copiedKey === key}
                          onCopy={() => copy(key, entry.password_plain)}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <p className="flex items-start gap-2 text-gray-500 text-xs">
              <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {t('files.passwords.privacyNote')}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
